import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled || open
          ? "bg-obsidian/85 backdrop-blur-xl border-b border-white/10 shadow-2xl"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 sm:h-28 items-center justify-between">
          <Link to="/" data-testid="nav-brand" className="group flex items-center">
            <img
              src="/assets/ltec-logo-navbar.png"
              alt="LTEC — Leo Tidwell Excavating Corp. Underground Utilities"
              className="h-16 sm:h-[5.25rem] w-auto transition-opacity group-hover:opacity-85"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                data-testid={link.testId}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium transition-colors hover:text-white ${
                    isActive ? "text-blueprint" : "text-slate-300"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
            <Link
              to="/request-bid"
              data-testid="nav-cta-rfq-button"
              className="ml-3 inline-flex items-center gap-1.5 bg-brand px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-light"
            >
              Request a Bid
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-obsidian/95 backdrop-blur-xl"
            aria-label="Mobile"
          >
            <div className="px-4 py-4 space-y-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  data-testid={`${link.testId}-mobile`}
                  className={({ isActive }) =>
                    `block px-3 py-3 font-display text-2xl font-semibold uppercase tracking-wide transition-colors ${
                      isActive ? "text-blueprint" : "text-slate-200 hover:text-white"
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              ))}
              <Link
                to="/request-bid"
                data-testid="nav-cta-rfq-button-mobile"
                className="mt-2 flex items-center justify-center gap-2 bg-brand px-4 py-3 text-sm font-semibold text-white"
              >
                Request a Bid <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
